import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  Header,
  VStack,
} from "@repo/ui";
import {
  AVATAR_ITEMS,
  ROUTESWEB,
  peekAinaReturnPath,
  resolveAinaSourceBreadcrumb,
} from "@repo/utils";

import { useDoubtData } from "../../lib/DoubtContext";
import { tryReturnToAinaFromDoubtsChat } from "../../lib/ainaDoubtsReturnWeb";
import { useIsMweb } from "../../hooks/useIsMweb";
import {
  GetAnswersChat,
  type GetAnswersChatCloseOptions,
  type GetAnswersChatHandle,
} from "../doubts-dashboard/GetAnswersChat/GetAnswersChat";
import type { GetAnswersChatSession } from "../doubts-dashboard/GetAnswersChat/types";
import { useTheme } from "@repo/tokens";

import "./ainaDoubtChat.css";

export type AinaDoubtChatScreenProps = {
  initialAinaChatSession: GetAnswersChatSession | null;
  onInitialAinaChatConsumed?: () => void;
};
const AinaDoubtChatScreen = ({
  initialAinaChatSession,
  onInitialAinaChatConsumed,
}: AinaDoubtChatScreenProps) => {
  const navigate = useNavigate();
  const doubtSession = useDoubtData();
  const isMweb = useIsMweb();
  const { t } = useTranslation("doubts");
  const { theme } = useTheme();
  const [chatSession, setChatSession] =
    React.useState<GetAnswersChatSession | null>(initialAinaChatSession);
  const chatRevokeUrlRef = React.useRef<string | null>(
    initialAinaChatSession?.kind === "image"
      ? initialAinaChatSession.imageUrl
      : null,
  );
  const getAnswersChatRef = React.useRef<GetAnswersChatHandle>(null);

  /** Resolved once on mount; the pre-AINA path doesn't change while the chat is open. */
  const sourceCrumbs = React.useMemo(
    () => resolveAinaSourceBreadcrumb(peekAinaReturnPath()),
    [],
  );

  React.useLayoutEffect(() => {
    if (!initialAinaChatSession) return;
    if (initialAinaChatSession.kind === "image") {
      chatRevokeUrlRef.current = initialAinaChatSession.imageUrl;
    } else {
      chatRevokeUrlRef.current = null;
    }
    setChatSession(initialAinaChatSession);
    onInitialAinaChatConsumed?.();
  }, [initialAinaChatSession, onInitialAinaChatConsumed]);

  const handleCloseChat = React.useCallback(
    (options?: GetAnswersChatCloseOptions) => {
      if (chatRevokeUrlRef.current) {
        URL.revokeObjectURL(chatRevokeUrlRef.current);
        chatRevokeUrlRef.current = null;
      }
      setChatSession(null);
      if (options?.navigateToHref != null && options.navigateToHref !== "") {
        navigate(options.navigateToHref, { replace: true });
        return;
      }
      if (!tryReturnToAinaFromDoubtsChat(navigate)) {
        navigate(`/${ROUTESWEB.APP.DOUBTS}`, { replace: true });
      }
    },
    [navigate],
  );

  const ainaMentorPath = `/${ROUTESWEB.APP.AINA_MENTOR}`;

  const handleSourceCrumbClick = React.useCallback(
    (href: string) =>
      (event: React.MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault();
        getAnswersChatRef.current?.requestBreadcrumbNavigation(href);
      },
    [],
  );

  const handleAinaMentorCrumbClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>): void => {
      event.preventDefault();
      getAnswersChatRef.current?.requestBreadcrumbNavigation(ainaMentorPath);
    },
    [ainaMentorPath],
  );

  const breadcrumb = React.useMemo(
    () => (
      <Breadcrumb>
        {sourceCrumbs.map((crumb) => (
          <React.Fragment key={crumb.key}>
            <BreadcrumbItem>
              <BreadcrumbLink
                href={crumb.href}
                onClick={handleSourceCrumbClick(crumb.href)}
              >
                {t(crumb.labelKey)}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </React.Fragment>
        ))}
        <BreadcrumbItem>
          <BreadcrumbLink
            href={ainaMentorPath}
            onClick={handleAinaMentorCrumbClick}
            color={theme.colors.primary[20]}
          >
            {t("aina_breadcrumb_aina_mentor")}
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
    ),
    [
      ainaMentorPath,
      handleAinaMentorCrumbClick,
      handleSourceCrumbClick,
      sourceCrumbs,
      t,
      theme,
    ],
  );

  if (!chatSession) {
    return null;
  }

  return (
    <VStack
      className={
        isMweb
          ? "aina-doubt-chat-root aina-doubt-chat-root--mweb"
          : "aina-doubt-chat-root"
      }
    >
      {!isMweb && (
        <div className="aina-doubt-chat-header">
          <Header
            breadcrumb={breadcrumb}
            rightIcon="userProfileIcon"
            rightIconItems={AVATAR_ITEMS}
            showCenter={false}
          />
        </div>
      )}
      <div className="aina-doubt-chat-body">
        <GetAnswersChat
          ref={getAnswersChatRef}
          session={chatSession}
          userFirstName={doubtSession.firstName ?? ""}
          onClose={handleCloseChat}
          layout={isMweb ? "fullscreen" : "embedded"}
          ainaEmbedded={!isMweb}
        />
      </div>
    </VStack>
  );
};

export default AinaDoubtChatScreen;
